/**
 * Display-only local datetime helpers.
 * Use these in UI labels/tables. Keep raw UTC values for booking save/update payloads.
 */
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

/** App-wide display formats */
export const LOCAL_DATE_FORMAT = "DD-MM-YYYY";
export const LOCAL_TIME_FORMAT = "hh:mm A";
export const LOCAL_DATETIME_FORMAT = "DD-MM-YYYY hh:mm A";

const inputFormats = [
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DD hh:mm A",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
  "YYYY-MM-DDTHH:mm:ssZ",
  "DD/MM/YYYY",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY hh:mm A",
  "MM/DD/YYYY",
  "MM/DD/YYYY HH:mm:ss",
  "MM/DD/YYYY hh:mm A",
  "DD-MM-YYYY",
  "DD-MM-YYYY HH:mm:ss",
  "DD-MM-YYYY hh:mm A",
  "MM-DD-YYYY",
  "MM-DD-YYYY HH:mm:ss",
  "MM-DD-YYYY hh:mm A",
];

const hasExplicitOffset = (value: string) =>
  /([zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());

const looksLikeDateOnly = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ||
  /^\d{2}[-/]\d{2}[-/]\d{4}$/.test(value.trim());

/**
 * Parse API/server timestamps into the browser's local timezone.
 * - ISO with Z/offset → converted to local
 * - Datetime without timezone → treated as UTC, then local
 * - Date-only → kept as calendar date (no day shift)
 */
export function parseToLocal(
  value: string | number | Date | Dayjs | null | undefined,
): Dayjs | null {
  if (value == null || value === "") return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.local() : null;
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof value === "number") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // Explicit timezone / ISO with Z
  if (hasExplicitOffset(raw) || raw.includes("T")) {
    const parsed = dayjs(raw);
    if (parsed.isValid()) return parsed.local();
  }

  // Strict known formats
  const strict = dayjs(raw, inputFormats, true);
  if (strict.isValid()) {
    if (looksLikeDateOnly(raw)) return strict;
    // Datetime without zone from backend → assume UTC wall clock
    return strict.utc(true).local();
  }

  // Fallback native parse
  const loose = dayjs(raw);
  return loose.isValid() ? loose.local() : null;
}

export function formatLocalDate(
  value: string | number | Date | Dayjs | null | undefined,
  format = LOCAL_DATE_FORMAT,
  fallback = "-",
): string {
  const parsed = parseToLocal(value);
  return parsed ? parsed.format(format) : fallback;
}

export function formatLocalTime(
  value: string | number | Date | Dayjs | null | undefined,
  format = LOCAL_TIME_FORMAT,
  fallback = "-",
): string {
  const parsed = parseToLocal(value);
  return parsed ? parsed.format(format) : fallback;
}

export function formatLocalDateTime(
  value: string | number | Date | Dayjs | null | undefined,
  format = LOCAL_DATETIME_FORMAT,
  fallback = "-",
): string {
  const parsed = parseToLocal(value);
  return parsed ? parsed.format(format) : fallback;
}

/** Convert a value to UTC ISO with Z suffix: `2026-07-24T16:25:00Z` */
export function toUtcZ(
  value: string | number | Date | Dayjs | null | undefined,
): string | null {
  if (value == null || value === "") return null;

  let parsed: Dayjs | null = null;
  if (dayjs.isDayjs(value)) {
    parsed = value.isValid() ? value : null;
  } else if (value instanceof Date || typeof value === "number") {
    parsed = dayjs(value);
    parsed = parsed.isValid() ? parsed : null;
  } else {
    const raw = String(value).trim();
    if (!raw) return null;
    // Prefer UTC parse so already-UTC values keep the same wall clock
    parsed = dayjs.utc(raw);
    if (!parsed.isValid()) {
      parsed = dayjs(raw);
    }
  }

  if (!parsed?.isValid()) return null;
  return parsed.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
}

/** Convert a local datetime back to UTC ISO for API writes. */
export function toUtcIso(
  value: string | number | Date | Dayjs | null | undefined,
): string | null {
  return toUtcZ(value);
}
